'use client';
import React, { useEffect, useState } from 'react';
import { Box } from '@radix-ui/themes';
import { RenderFields } from './RenderFields';
import { ApiAttribute } from '@context/openapi/types';
import InputLabel from '@components/ui/DynamicForm/InputLabel';
import { isFieldFilled, isFieldVisibilityOverridden } from '@/services/util';
import { useExplorer } from '@/hooks/useExplorer';
import { clearAttribute } from '@/services/ClearForm';

interface ObjectInput {
  field: ApiAttribute;
  onChange: (field: ApiAttribute, value: string) => void;
  onFilterChange?: (field: ApiAttribute, value: string) => void;
  isEmptyAttributesVisible: boolean;
}

export const ObjectInput: React.FC<ObjectInput> = ({
  field,
  onChange,
  onFilterChange,
  isEmptyAttributesVisible,
}) => {
  const {
    setEndpointAttributes,
    endpointAttributes,
    endpoint,
    visibleFieldsOverride,
  } = useExplorer();
  const [expanded, setExpanded] = React.useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const expand = () => {
    setExpanded(!expanded);
  };

  const handleClear = () => {
    if (endpoint && endpointAttributes) {
      const updatedEndpointAttributes = clearAttribute(
        endpoint?.method,
        endpointAttributes,
        field
      );
      setEndpointAttributes({ ...updatedEndpointAttributes });
      // localStorage.setItem(
      //   `input_cache`,
      //   atob(base64EncodedFormData(updatedEndpointAttributes, endpoint))
      // );
    }
  };

  useEffect(() => {
    if (isEmptyAttributesVisible) {
      setExpanded(isFieldFilled(field));
    } else {
      setExpanded(isFieldVisibilityOverridden(field, visibleFieldsOverride));
    }
  }, [endpointAttributes]);

  const handleFocus = (hovered: boolean) => {
    setIsFocused(hovered);
  };

  return (
    <Box
      key={field.name}
      className={`
        bg-[#d3e4e6]
        border-b border-solid border-form-border
        border-l border-r
      `}
    >
      <div
        className={`pl-2 flex gap-2 justify-between w-full hover:bg-[#d3e4e6] group ${isFocused && 'bg-[#d3e4e6]'}`}
        onClick={expand}
      >
        <InputLabel field={field} />
        <div className="ml-auto flex-1 cursor-pointer">
          {!expanded && (
            <button
              className="text-xs py-1 pl-1 text-neutral-400 opacity-100 focus-visible:outline-shade-aqua-300 focus-visible:px-2"
              onFocus={() => handleFocus(true)}
              onBlur={() => handleFocus(false)}
            >
              Click to expand
            </button>
          )}
          {expanded && (
            <button
              className="text-xs  float-right py-1 mr-2 focus-visible:outline-shade-aqua-300 focus-visible:px-2"
              onClick={handleClear}
            >
              Clear
            </button>
          )}
        </div>
      </div>
      {expanded && (
        <div className="px-4 py-3 flex flex-col relative rounded-lg bg-[#f7fbfb] my-1 mx-2 [&>*:first-child]:border-t [&>*:first-child]:border-solid [&>*:first-child]:border-form-border">
          {field.child?.map((child) => (
            <Box key={child.key} className="w-full mx-auto">
              <RenderFields
                field={child}
                onChange={onChange}
                onFilterChange={onFilterChange}
                isEmptyAttributesVisible={
                  isFieldVisibilityOverridden(field, visibleFieldsOverride)
                    ? true
                    : isEmptyAttributesVisible
                }
              />
            </Box>
          ))}
        </div>
      )}
    </Box>
  );
};

export default ObjectInput;
